import {
    scoreCommandSearchFields,
    type CommandSearchContext,
    type CommandSearchProvider,
    type CommandSearchResult,
} from '../../components/panel-command-search';
import {queryPanelEntries} from '../registry';
import {toPanelEntryContext} from '../access';
import type {PanelAccessServices} from '../types';

export interface PanelActionsServices extends PanelAccessServices {
    openSettings: () => void;
    openSupportChat: () => void;
    openLogoutDialog: () => void;
    openQuickOrder?: () => void;
    panelEntries?: unknown[];
}

const getServices = (ctx: CommandSearchContext): PanelActionsServices | undefined =>
    ctx.services as PanelActionsServices | undefined;

/** Maps catalog action entries to live command-search results using injected services. */
export const buildPanelActionResultsFromRegistry = (
    ctx: CommandSearchContext,
    services: PanelActionsServices,
): CommandSearchResult[] => {
    const entryCtx = toPanelEntryContext(ctx);
    const catalogActions = queryPanelEntries({
        surface: 'command-search',
        providerId: 'panel-actions',
        ctx: entryCtx,
    });

    const results: CommandSearchResult[] = [];

    catalogActions.forEach((entry) => {
        let onSelect: (() => void) | undefined;

        switch (entry.actionKey) {
            case 'settings':
                onSelect = services.openSettings;
                break;
            case 'support':
                onSelect = services.openSupportChat;
                break;
            case 'logout':
                onSelect = services.openLogoutDialog;
                break;
            case 'quick-order':
                onSelect = services.openQuickOrder;
                break;
            default:
                break;
        }

        if (!onSelect) {
            return;
        }

        const score = scoreCommandSearchFields(ctx.query, {
            title: entry.title,
            subtitle: entry.subtitle,
            keywords: entry.keywords,
            category: entry.commandSearch?.category ?? 'actions',
            type: entry.commandSearch?.badge,
        });
        if (score <= 0) {
            return;
        }

        results.push({
            id: entry.id,
            category: entry.commandSearch?.category ?? 'actions',
            title: entry.title,
            subtitle: entry.subtitle,
            keywords: entry.keywords,
            badge: entry.commandSearch?.badge,
            badgeColor: entry.commandSearch?.badgeColor,
            icon: entry.icon,
            score,
            providerId: 'panel-actions',
            recentMeta: entry.actionKey ? {actionKey: entry.actionKey} : undefined,
            onSelect,
        });
    });

    return results;
};

export const panelActionsProviderFromRegistry: CommandSearchProvider = {
    id: 'panel-actions',
    category: 'actions',
    priority: 10,
    search: (ctx) => {
        const services = getServices(ctx);
        if (!services) {
            return [];
        }
        return buildPanelActionResultsFromRegistry(ctx, services);
    },
};
